import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatChipsModule } from '@angular/material/chips';
import { MatError, MatFormField, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption, MatSelect } from '@angular/material/select';
import { TranslocoDirective } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { SupportedLocale } from '../../domain/signup';
import { SignupStore } from '../../domain/signup.store';

/** 400ms: long enough that a typed word is one request, short enough to feel live. */
const KEY_CHECK_DEBOUNCE_MS = 400;

/**
 * Step two: the organization's display name, its permanent key, and its language.
 *
 * **The key is checked live and the warning is not decoration.** It becomes the tenant's realm name,
 * its bucket prefix, the discriminator in every composite key and the hostname the customer's own
 * users type — so it cannot be changed afterwards without orphaning every id that names it. The
 * contract says the form must say so plainly next to this field, and this is where that is done.
 *
 * **The check is debounced and forwarded unvalidated.** The endpoint is specified to answer
 * `available: false` with an `errorId` for partial or malformed input, so the form renders the
 * server's reason rather than deciding for itself what a valid key is. A second definition of the
 * slug rule here would eventually disagree with provisioning, and the customer would meet that
 * disagreement at submit time.
 *
 * `errorId` is rendered as a transloco key — that is what the contract means by returning one — and
 * the suggestions are clickable, because a customer whose first choice is taken should not have to
 * invent a second.
 */
@Component({
  selector: 'pp-organization-step',
  imports: [ReactiveFormsModule, MatFormField, MatLabel, MatError, MatHint, MatInput, MatSelect, MatOption, MatChipsModule, MatIcon, MatProgressSpinner, TranslocoDirective],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.signup.organization'">
      <p class="step-lead">{{ t('lead') }}</p>

      <form [formGroup]="form" class="organization-step-form">
        <mat-form-field>
          <mat-label>{{ t('name') }}</mat-label>
          <input id="signup-org-name" matInput formControlName="name" autocomplete="organization" />
          @if (form.controls.name.hasError('required') && form.controls.name.touched) {
            <mat-error>{{ t('nameRequired') }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ t('key') }}</mat-label>
          <input id="signup-org-key" matInput formControlName="key" autocomplete="off" spellcheck="false" />
          <mat-hint>{{ t('keyHint') }}</mat-hint>
          @if (form.controls.key.hasError('required') && form.controls.key.touched) {
            <mat-error>{{ t('keyRequired') }}</mat-error>
          }
        </mat-form-field>

        <!-- The permanence warning. Stated as its own paragraph rather than as a hint, because a
             hint under a field is read as guidance and this is a decision the customer cannot
             revisit. -->
        <p class="key-permanence" test-id="signup-key-permanence">
          <mat-icon>warning</mat-icon>
          <span>{{ t('keyPermanent') }}</span>
        </p>

        @if (store.checkingKey()) {
          <p class="key-checking" test-id="signup-key-checking">
            <mat-progress-spinner diameter="16" mode="indeterminate" />
            <span>{{ t('keyChecking') }}</span>
          </p>
        } @else if (availability(); as result) {
          @if (result.available) {
            <p class="key-available" test-id="signup-key-available">{{ t('keyAvailable', { key: result.key }) }}</p>
          } @else {
            <!-- The server's own errorId, rendered as a key. Two spellings of "this key is
                 reserved" — one here and one in Admin — would show the customer a raw key whenever
                 the two disagreed. -->
            <p class="key-taken" test-id="signup-key-taken">{{ result.errorId ? t('errorId.' + result.errorId) : t('keyUnavailable') }}</p>
            @if (result.suggestions.length > 0) {
              <p class="key-suggestions-label">{{ t('keySuggestions') }}</p>
              <mat-chip-set test-id="signup-key-suggestions">
                @for (suggestion of result.suggestions; track suggestion) {
                  <mat-chip-option [attr.test-id]="'signup-key-suggestion-' + suggestion" (click)="useSuggestion(suggestion)">{{ suggestion }}</mat-chip-option>
                }
              </mat-chip-set>
            }
          }
        }

        <mat-form-field>
          <mat-label>{{ t('locale') }}</mat-label>
          <mat-select id="signup-org-locale" formControlName="defaultLocale">
            @for (locale of locales; track locale) {
              <mat-option [value]="locale">{{ t('locales.' + locale) }}</mat-option>
            }
          </mat-select>
          <mat-hint>{{ t('localeHint') }}</mat-hint>
        </mat-form-field>
      </form>
    </ng-container>
  `,
  styles: [
    `
      .organization-step-form {
        display: flex;
        flex-direction: column;
        row-gap: 8px;
        max-width: 460px;
      }

      .key-permanence {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        font-size: 0.85rem;
      }

      .key-checking {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.85rem;
        opacity: 0.75;
      }

      .key-available {
        color: var(--mat-sys-primary, #1b5e20);
        font-size: 0.9rem;
      }

      .key-taken {
        color: var(--mat-sys-error, #b3261e);
        font-size: 0.9rem;
      }

      .key-suggestions-label {
        font-size: 0.85rem;
        opacity: 0.75;
        margin-bottom: 4px;
      }
    `,
  ],
})
export class OrganizationStepComponent implements OnInit {
  readonly store = inject(SignupStore);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly locales = Object.values(SupportedLocale);

  readonly form = this.formBuilder.nonNullable.group({
    name: ['', Validators.required],
    key: ['', Validators.required],
    defaultLocale: [SupportedLocale.EN, Validators.required],
  });

  /** The store's answer, but only for the key currently in the field — see {@link ngOnInit}. */
  readonly availability = this.store.keyAvailability;

  ngOnInit(): void {
    const organization = this.store.draft().organization;
    if (organization) {
      this.form.patchValue({
        name: organization.name ?? '',
        key: organization.key ?? '',
        defaultLocale: (organization.defaultLocale as SupportedLocale) ?? SupportedLocale.EN,
      });
    }

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.push());

    // Debounced and distinct: one request per key the customer settles on, not one per keystroke.
    // `takeUntilDestroyed` matters more than usual here — the funnel is left by a full-page
    // authentication redirect, and a pending timer firing into a destroyed component would patch a
    // store nothing is rendering.
    this.form.controls.key.valueChanges.pipe(debounceTime(KEY_CHECK_DEBOUNCE_MS), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((key) => this.store.checkKey(key.trim()));
  }

  /** Takes a suggestion the server offered, and re-checks it — it was free a moment ago, not now. */
  useSuggestion(suggestion: string): void {
    this.form.patchValue({ key: suggestion });
    this.store.checkKey(suggestion);
  }

  private push(): void {
    const value = this.form.getRawValue();
    this.store.updateDraft({
      organization: { name: value.name.trim(), key: value.key.trim(), defaultLocale: value.defaultLocale },
    });
  }
}
